import { FORM_DATA_KEYS_MAP, retryCount } from '@/shared/constants/api.constants'
import HttpService from '../HttpService'
import { SendFormDataFields, SendFormDataKeys, SendFormDataResult } from '@/shared/types/api.types'
import {
  CountrysResponse,
  GetQatarWordings,
  CountriesRawResponse,
  RelationType,
  RelationsResponse,
} from '@/shared/types/response.types'
import {
  CountriesMappingType,
  EmailDomain,
  EnterpriseContact,
  EntryValuesType,
  MaritalStatus,
  WordingParams,
} from '@/shared/types/other.types'
import { BACKUP_ENTERPRISE_CONTACTS } from '@/shared/constants/contacts.constants'
import { CacheService, useCacheService } from '@/shared/composables/useCacheService'
import { AxiosInstance, AxiosResponse } from 'axios'
import { entryValues } from '@/shared/composables/useEntry'
import mapping from '@/shared/assets/maps/country-mapping.json'

type FallbackFlagsType = { [key: number]: string }

const fallbackFlags: FallbackFlagsType = {
  532: 'netherlands-antilles',
  997: 'n_cyprus',
}

const getFlag = (code: string) => {
  const fromMapping = (mapping as CountriesMappingType)[code]

  if (fromMapping) {
    return fromMapping.flag
  }

  return fallbackFlags[Number(code)] || 'none'
}

export class CommonService extends HttpService {
  private $cacheService: CacheService

  constructor(fetcher: AxiosInstance) {
    super(fetcher)
    this.$cacheService = useCacheService()
  }

  async sendFormData(params: SendFormDataFields): Promise<SendFormDataResult> {
    const formData = Object.entries(params).reduce((formDataString, [key, value]) => {
      //TODO: Research how to change `as`
      const serializedKey = FORM_DATA_KEYS_MAP[key as SendFormDataKeys] ?? key
      const fieldsString = `fields[${serializedKey}]=${value}`
      return formDataString ? `${formDataString}&${fieldsString}` : fieldsString
    }, '')

    // @ts-ignore
    return await this.post('/sendformdata', formData, { retry: retryCount })
  }

  async getAbDistribution<T>(code: string): Promise<{ experiment: string; group: T | null }[]> {
    const { data } = await this.get<AxiosResponse>('getabdistribution', {
      params: { code },
    })
    return data.list
  }

  async getQatarWordings(params: WordingParams): Promise<GetQatarWordings> {
    const { data } = await this.get<AxiosResponse>('getqatarwordings', { params })
    return data
  }

  async getWordingLink(wordingParams: WordingParams, cacheKey?: string): Promise<string> {
    if (cacheKey) {
      const cachedLink = this.$cacheService.get(cacheKey)
      if (cachedLink) return cachedLink
    }

    const link = await this.getQatarWordings(wordingParams).then((res) => res.Wordings[0].Link)

    if (link && cacheKey) {
      this.$cacheService.set(cacheKey, link, { hours: 12 })
    }

    return link
  }

  async getPrivacyPolicyLink(): Promise<string> {
    return await this.getWordingLink(
      { categoryid: '1', docname: 'privpolicy' },
      'privacy_policy_link'
    )
  }

  async getTermsAndConditionsLink(): Promise<string> {
    return await this.getWordingLink(
      { categoryid: '17', docname: 'visitorscheckout' },
      'visitors_checkout_link'
    )
  }

  async getBenefitsLink(): Promise<string> {
    return await this.getWordingLink(
      { categoryid: '17', docname: 'visitorstob' },
      'visitors_benefits_link'
    )
  }

  async getPolicyWordingInbound(): Promise<string> {
    return await this.getWordingLink(
      { categoryid: '7', docname: 'tavelinbtc' },
      'inbound_policy_wording'
    )
  }

  async getRelations(locale: LocaleType): Promise<RelationsResponse[]> {
    const CACHE_KEY = `relations_${locale}`
    const cachedRelations = this.$cacheService.get(CACHE_KEY)

    if (cachedRelations) return cachedRelations

    const { data } = await this.get<AxiosResponse>('/getrelationlist')

    const relations = data.list
      .sort((a: RelationType, b: RelationType) => a.acCode.localeCompare(b.acCode))
      .map((relations: RelationType) => ({
        code: relations.acCode,
        desc: relations.acDesc,
      }))
      .filter((item: RelationsResponse) => item.code !== '009')

    if (relations.length > 0) {
      this.$cacheService.set(CACHE_KEY, relations, { hours: 12 })
    }
    return relations
  }

  async getMaritalStatuses(locale: LocaleType): Promise<MaritalStatus[]> {
    const CACHE_KEY = `marital_status_list_${locale}`
    const cachedMaritalStatuses = this.$cacheService.get(CACHE_KEY)

    if (cachedMaritalStatuses?.length > 0) return cachedMaritalStatuses

    const { data } = await this.get<AxiosResponse>('/maritalstatus')

    const maritalStatuses = data.list ?? []

    if (maritalStatuses.length > 0) {
      this.$cacheService.set(CACHE_KEY, maritalStatuses, { hours: 12 })
    }
    return maritalStatuses
  }

  async getVisitorsEntryType(locale: LocaleType): Promise<EntryValuesType[]> {
    const CACHE_KEY = `durations_${locale}`
    const cachedDurations = this.$cacheService.get(CACHE_KEY)

    if (cachedDurations) return cachedDurations

    const { data } = await this.get<AxiosResponse>('getvisitorsentrytype')
    const durations = data.durations

    if (!durations.length) return entryValues

    if (durations.length > 0) {
      this.$cacheService.set(CACHE_KEY, durations)
    }
    return durations
  }

  async getNationalities(locale: LocaleType): Promise<CountrysResponse[]> {
    const CACHE_KEY = `flag-nationalities_${locale}`
    const cachedNationalities = this.$cacheService.get(CACHE_KEY)

    if (cachedNationalities) return cachedNationalities

    const { data } = await this.get<AxiosResponse>('/listcountries', {
      params: {
        productCode: 'visitors',
        locale,
      },
    })

    const nationalities = data.list
      .sort((a: CountriesRawResponse, b: CountriesRawResponse) => {
        if (a.popularNationality && b.popularNationality) {
          return a.popularNationality - b.popularNationality
        }
        if (a.popularNationality) return -1
        if (b.popularNationality) return 1
        return a.countryCode.localeCompare(b.countryCode)
      })
      .map((nationality: CountriesRawResponse) => ({
        code: nationality.countryCode,
        desc: nationality.countryName,
        flag: getFlag(nationality.countryCode),
        popularNationality: nationality.popularNationality,
        popularCountry: nationality.popularCountry,
        searchString: nationality.searchString?.toLowerCase() ?? '',
      }))

    if (nationalities.length > 0) {
      this.$cacheService.set(CACHE_KEY, nationalities, { hours: 12 })
    }

    return nationalities
  }

  async sendCallback() {
    return Promise.resolve(true)
  }

  async getContacts(): Promise<EnterpriseContact[]> {
    try {
      const CACHE_KEY = 'enterprise_contacts'
      const cachedContacts = this.$cacheService.get(CACHE_KEY)

      if (cachedContacts) {
        return cachedContacts
      }

      const { data } = await this.get<AxiosResponse>('getcontacts', { params: { region: 'qatar' } })
      const contacts = data.contacts

      if (contacts.length) {
        this.$cacheService.set(CACHE_KEY, contacts, { hours: 12 })
      }

      return contacts
    } catch {
      return BACKUP_ENTERPRISE_CONTACTS
    }
  }

  async getEmailDomains(): Promise<EmailDomain[]> {
    const CACHE_KEY = 'email_domains'
    const cachedDomains = this.$cacheService.get(CACHE_KEY)
    if (cachedDomains) return cachedDomains

    const { data } = await this.get<AxiosResponse>('getemaildomains')
    if (data.domains.length) {
      this.$cacheService.set(CACHE_KEY, data.domains, { hours: 12 })
    }
    return data.domains
  }
}
